import { Box, Button, CircularProgress, Modal, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useMemo, useState } from "react";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { modalStyles, useStyles } from "./NotificationsModal.styles";
import { UsersEntity } from "entities/Users/UsersEntity";
import { AspenNotifications } from "./components/AspenNotifications";
import { BirchNotifications } from "./components/BirchNotifications";
import NoData from "components/NoData/NoData.view";
import { ModuleNotificationsEnum } from "constants/constants";

const NotificationsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const { enabledNotificationsIds, rcaEnabled } = useContext(CorporateContext);
    const classes = useStyles();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(ModuleNotificationsEnum.ASPEN);

    const { loading, error, birchNotificationSettings, aspenNotificationSettings, refetch } =
        UsersEntity.GetUserNotificationSettings(enabledNotificationsIds);

    const tabsComponents = useMemo(
        () => ({
            [ModuleNotificationsEnum.ASPEN]: {
                component: <AspenNotifications refetch={refetch} notifications={aspenNotificationSettings} />,
            },
            [ModuleNotificationsEnum.BIRCH]: {
                component: <BirchNotifications refetch={refetch} notifications={birchNotificationSettings} />,
            },
        }),
        [refetch, aspenNotificationSettings, birchNotificationSettings],
    );

    const handleOnClose = () => {
        onClose();
    };

    const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
        setActiveTab(newTab);
    };

    const showAspenTab = useMemo(() => !!aspenNotificationSettings?.length, [aspenNotificationSettings]);
    const showBirchTab = useMemo(
        () => !!birchNotificationSettings?.length && rcaEnabled,
        [birchNotificationSettings, rcaEnabled],
    );

    const tab = useMemo(() => {
        if (loading) {
            return (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={24} color="info" />
                </div>
            );
        } else if (error) {
            return (
                <NoData
                    title={t(
                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.update_error",
                    )}
                />
            );
        } else {
            if (!showAspenTab && !showBirchTab) {
                return (
                    <NoData
                        title={t(
                            "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.no_notifications_enabled",
                        )}
                    />
                );
            }

            return tabsComponents[activeTab].component;
        }
    }, [loading, error, showAspenTab, showBirchTab, activeTab, t, tabsComponents]);

    useEffect(() => {
        if (showAspenTab) {
            setActiveTab(ModuleNotificationsEnum.ASPEN);
        } else {
            setActiveTab(ModuleNotificationsEnum.BIRCH);
        }
    }, [showAspenTab]);

    return (
        <Modal open={open} onClose={handleOnClose} disableEscapeKeyDown>
            <Box sx={modalStyles}>
                <div className={classes.modalHeader}>
                    {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.title")}
                </div>
                <Typography className={classes.text}>
                    {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.description")}
                </Typography>
                <div className={classes.modalBody}>
                    <Tabs value={activeTab} onChange={handleTabChange} className={classes.tabs}>
                        {showAspenTab && <Tab value={ModuleNotificationsEnum.ASPEN} label={"ASPEN"} />}
                        {showBirchTab && <Tab value={ModuleNotificationsEnum.BIRCH} label={"BIRCH"} />}
                    </Tabs>
                    {tab}
                </div>
                <div className={classes.footer}>
                    <Button variant="contained" color="info" className={classes.submitButton} onClick={handleOnClose}>
                        {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.close")}
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};

export default NotificationsModal;
